import { UserSubscription } from '@griply/types';
import Dialog from '@mui/material/Dialog';
import { useDeleteAccount, useLogout } from 'features/user';
import React, { useCallback, useRef, useState } from 'react';
import { DeleteAccountSurveyDialog } from 'shared/dialogs/delete-account-survey';

import { DeleteAccountConfirm } from './delete-account-confirm';
import { DeleteAccountError } from './delete-account-error';
import { DeleteAccountLoading } from './delete-account-loading';
import { DeleteAccountReauth } from './delete-account-reauth';
import { DeleteAccountSubscriptionWarning } from './delete-account-subscription-warning';
import { useDeleteAccountReauth } from './use-delete-account-reauth';

enum DeleteAccountStep {
  Confirm = 'confirm',
  Reauth = 'reauth',
  Survey = 'survey',
}

export type DeleteAccountDialogProps = {
  open: boolean;
  blockingSubscription: UserSubscription | null;
  onClose: () => void;
};

export const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
  open,
  blockingSubscription,
  onClose,
}) => {
  const {
    submit: deleteAccount,
    isLoading,
    isError,
    errorKind,
    reset,
  } = useDeleteAccount();
  const logout = useLogout();
  const {
    provider,
    password,
    onChangePassword,
    errorMessage,
    isSubmitting,
    prepare,
    submitPassword,
    startSocialRedirect,
    reset: resetReauth,
  } = useDeleteAccountReauth();

  const [step, setStep] = useState<DeleteAccountStep>(
    DeleteAccountStep.Confirm,
  );
  const hasDeletedRef = useRef(false);

  const handleClose = useCallback(() => {
    if (isLoading) {
      return;
    }
    reset();
    resetReauth();
    hasDeletedRef.current = false;
    setStep(DeleteAccountStep.Confirm);
    onClose();
  }, [isLoading, onClose, reset, resetReauth]);

  const handleSignOut = useCallback(async () => {
    await logout();
    reset();
    onClose();
  }, [logout, onClose, reset]);

  // Confirming is the decision. A recent login (e.g. just back from the social
  // redirect) skips straight to the survey; otherwise reauth comes first.
  const onConfirm = useCallback(() => {
    void (async () => {
      const needsReauth = await prepare();
      setStep(
        needsReauth ? DeleteAccountStep.Reauth : DeleteAccountStep.Survey,
      );
    })();
  }, [prepare]);

  const onSubmitPassword = useCallback(() => {
    void (async () => {
      const ok = await submitPassword();
      if (ok) {
        setStep(DeleteAccountStep.Survey);
      }
    })();
  }, [submitPassword]);

  // Submitting and skipping the survey both delete; the ref keeps that to one
  // call. Backing out or dismissing routes to handleClose instead — no delete.
  const handleSurveyDelete = useCallback(() => {
    if (hasDeletedRef.current) {
      return;
    }
    hasDeletedRef.current = true;
    setStep(DeleteAccountStep.Confirm);
    void deleteAccount();
  }, [deleteAccount]);

  // The subscription warning outranks everything but an error; the remaining
  // branches are mutually exclusive in this order.
  const showSubscriptionWarning = !!blockingSubscription && !isError;
  const showLoading = !showSubscriptionWarning && isLoading;
  const showError =
    !showSubscriptionWarning && !showLoading && isError && !!errorKind;
  const showReauth =
    !showSubscriptionWarning &&
    !showLoading &&
    !showError &&
    step === DeleteAccountStep.Reauth;
  const showConfirm =
    !showSubscriptionWarning && !showLoading && !showError && !showReauth;

  return (
    <>
      <Dialog open={open} fullWidth onClose={handleClose}>
        {showSubscriptionWarning && blockingSubscription && (
          <DeleteAccountSubscriptionWarning
            subscription={blockingSubscription}
            onCancel={handleClose}
          />
        )}

        {showLoading && <DeleteAccountLoading />}

        {showError && errorKind && (
          <DeleteAccountError
            errorKind={errorKind}
            onRetry={deleteAccount}
            onSignOut={handleSignOut}
            onCancel={handleClose}
          />
        )}

        {showReauth && (
          <DeleteAccountReauth
            provider={provider}
            password={password}
            onChangePassword={onChangePassword}
            errorMessage={errorMessage}
            isSubmitting={isSubmitting}
            onSubmitPassword={onSubmitPassword}
            onContinueSocial={startSocialRedirect}
            onCancel={handleClose}
          />
        )}

        {showConfirm && (
          <DeleteAccountConfirm onSubmit={onConfirm} onCancel={handleClose} />
        )}
      </Dialog>

      {step === DeleteAccountStep.Survey && (
        <DeleteAccountSurveyDialog
          onDelete={handleSurveyDelete}
          onCancel={handleClose}
        />
      )}
    </>
  );
};
